import { extractErrorMessage } from "../utils/acceptSuggestions";
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import StepDialog from "../components/StepDialog";
import ReadinessBadge from "../components/ReadinessBadge";
import AIPowerSearch from "../components/AIPowerSearch";
import FederatedFromBadge from "../components/FederatedFromBadge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  FileText,
  FolderOpen,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Download,
  Eye,
  Edit,
  Trash2,
  Plus,
  Search,
  Filter,
  History,
  Users,
  Calendar,
  Archive,
  RefreshCw,
  Shield,
  FileCheck,
  Upload,
  GitBranch,
  Lock,
  Unlock,
} from "lucide-react";

// Document Categories based on ISO 22301
const DOCUMENT_CATEGORIES = [
  { id: "policy", name: "Policy Documents", icon: Shield, color: "bg-sky-100 text-sky-700" },
  { id: "plan", name: "BC Plans", icon: FileText, color: "bg-blue-100 text-blue-700" },
  { id: "procedure", name: "Procedures", icon: FileCheck, color: "bg-emerald-100 text-emerald-700" },
  { id: "bia", name: "BIA Reports", icon: FileText, color: "bg-amber-100 text-amber-700" },
  { id: "risk", name: "Risk Assessments", icon: AlertTriangle, color: "bg-red-100 text-red-700" },
  { id: "training", name: "Training Materials", icon: Users, color: "bg-purple-100 text-purple-700" },
  { id: "audit", name: "Audit Reports", icon: FileCheck, color: "bg-slate-100 text-slate-700" },
  { id: "exercise", name: "Exercise Reports", icon: FileText, color: "bg-teal-100 text-teal-700" },
];

const RETENTION_PERIODS = [
  { value: "1_year", label: "1 Year" },
  { value: "3_years", label: "3 Years" },
  { value: "5_years", label: "5 Years" },
  { value: "7_years", label: "7 Years" },
  { value: "permanent", label: "Permanent" },
];

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft", color: "bg-slate-100 text-slate-700" },
  { value: "under_review", label: "Under Review", color: "bg-amber-100 text-amber-700" },
  { value: "approved", label: "Approved", color: "bg-emerald-100 text-emerald-700" },
  { value: "archived", label: "Archived", color: "bg-gray-100 text-gray-500" },
];

export default function DocumentManagementPage() {
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [aiSearchResults, setAiSearchResults] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadStep, setUploadStep] = useState(0);

  const [formData, setFormData] = useState({
    title: "",
    category: "",
    description: "",
    retention_period: "3_years",
    owner: "",
    review_frequency: "annual",
    next_review_date: "",
    status: "draft",
  });

  // Mock data for demonstration
  const mockDocuments = [
    {
      id: "1",
      title: "Business Continuity Policy",
      category: "policy",
      version: "2.1",
      status: "approved",
      owner: "John Smith",
      last_modified: "2025-01-15",
      next_review: "2025-07-15",
      retention_period: "permanent",
      downloads: 45,
      versions: [
        { version: "2.1", date: "2025-01-15", author: "John Smith", changes: "Annual review updates" },
        { version: "2.0", date: "2024-07-10", author: "Jane Doe", changes: "Major revision" },
        { version: "1.0", date: "2024-01-01", author: "John Smith", changes: "Initial version" },
      ],
    },
    {
      id: "2",
      title: "IT Disaster Recovery Plan",
      category: "plan",
      version: "3.0",
      status: "approved",
      owner: "Mike Johnson",
      last_modified: "2025-01-20",
      next_review: "2025-04-20",
      retention_period: "5_years",
      downloads: 32,
      versions: [
        { version: "3.0", date: "2025-01-20", author: "Mike Johnson", changes: "Cloud migration updates" },
        { version: "2.0", date: "2024-06-15", author: "Mike Johnson", changes: "Added RTO/RPO targets" },
      ],
    },
    {
      id: "3",
      title: "Crisis Communication Procedure",
      category: "procedure",
      version: "1.2",
      status: "under_review",
      owner: "Sarah Wilson",
      last_modified: "2025-01-25",
      next_review: "2025-07-25",
      retention_period: "3_years",
      downloads: 18,
      versions: [
        { version: "1.2", date: "2025-01-25", author: "Sarah Wilson", changes: "Updated contact list" },
      ],
    },
    {
      id: "4",
      title: "Q4 2024 BIA Report",
      category: "bia",
      version: "1.0",
      status: "approved",
      owner: "Emily Brown",
      last_modified: "2024-12-15",
      next_review: "2025-12-15",
      retention_period: "7_years",
      downloads: 28,
      versions: [
        { version: "1.0", date: "2024-12-15", author: "Emily Brown", changes: "Initial assessment" },
      ],
    },
    {
      id: "5",
      title: "Annual Risk Assessment 2025",
      category: "risk",
      version: "1.0",
      status: "draft",
      owner: "David Lee",
      last_modified: "2025-01-28",
      next_review: "2026-01-28",
      retention_period: "5_years",
      downloads: 5,
      versions: [],
    },
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/documents");
      if (response.data && response.data.length > 0) {
        setDocuments(response.data);
      } else {
        // Use mock data if no documents exist yet
        setDocuments(mockDocuments);
      }
    } catch (error) {
      console.error("Failed to fetch documents:", error);
      // Fallback to mock data on error
      setDocuments(mockDocuments);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getCategoryInfo = (categoryId) => {
    return DOCUMENT_CATEGORIES.find(c => c.id === categoryId) || DOCUMENT_CATEGORIES[0];
  };

  const getStatusInfo = (statusId) => {
    return STATUS_OPTIONS.find(s => s.value === statusId) || STATUS_OPTIONS[0];
  };

  const filteredDocuments = (aiSearchResults || documents).filter(doc => {
    const matchesSearch = !searchQuery || doc.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.owner?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === "all" || doc.category === categoryFilter;
    const matchesStatus = statusFilter === "all" || doc.status === statusFilter;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const handleUpload = async () => {
    if (!formData.title || !formData.category) {
      toast.error("Please fill in required fields");
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.post("/documents", formData);
      toast.success("Document created successfully!");
      setShowUploadDialog(false);
      resetForm();
      fetchData(); // Refresh the list
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to create document");
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Are you sure you want to delete "${doc.title}"?`)) return;
    
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Document deleted");
      fetchData();
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to delete document");
      toast.error(message);
    }
  };

  const handleDownload = async (doc) => {
    try {
      await api.post(`/documents/${doc.id}/download`);
      toast.success(`Downloaded: ${doc.title}`);
      // In a real implementation, this would trigger a file download
    } catch (error) {
      toast.error("Failed to record download");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      category: "",
      description: "",
      retention_period: "3_years",
      owner: "",
      review_frequency: "annual",
      next_review_date: "",
      status: "draft",
    });
  };

  const handleViewVersions = (doc) => {
    setSelectedDocument(doc);
    setShowVersionDialog(true);
  };

  // Calculate stats
  const stats = {
    total: documents.length,
    approved: documents.filter(d => d.status === "approved").length,
    pending: documents.filter(d => d.status === "under_review" || d.status === "draft").length,
    dueForReview: documents.filter(d => {
      if (!d.next_review) return false;
      const reviewDate = new Date(d.next_review);
      const today = new Date();
      const daysUntil = Math.ceil((reviewDate - today) / (1000 * 60 * 60 * 24));
      return daysUntil <= 30 && daysUntil > 0;
    }).length,
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="document-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-blue-100 text-blue-700">ISO 22301 Clause 7.5</Badge>
            <Badge variant="outline">Document Control</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Document Management</h1>
          <p className="text-slate-600 mt-1">
            Version control, retention policies, and distribution tracking for BCMS documentation
          </p>
          <div className="mt-2">
            <ReadinessBadge
              total={documents.length}
              complete={documents.filter(d => d.status === "approved").length}
              label="Documents"
            />
          </div>
        </div>
        <Button 
          onClick={() => setShowUploadDialog(true)}
          className="bg-sky-700 hover:bg-sky-800"
          data-testid="upload-document-btn"
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-sky-100 rounded-lg">
                <FolderOpen className="w-5 h-5 text-sky-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Documents</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Approved</p>
                <p className="text-2xl font-bold text-slate-900">{stats.approved}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pending Review</p>
                <p className="text-2xl font-bold text-slate-900">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Due for Review</p>
                <p className="text-2xl font-bold text-slate-900">{stats.dueForReview}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="documents" className="space-y-6">
        <TabsList>
          <TabsTrigger value="documents" data-testid="documents-tab">
            <FileText className="w-4 h-4 mr-2" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="categories" data-testid="categories-tab">
            <FolderOpen className="w-4 h-4 mr-2" />
            Categories
          </TabsTrigger>
          <TabsTrigger value="retention" data-testid="retention-tab">
            <Archive className="w-4 h-4 mr-2" />
            Retention Policy
          </TabsTrigger>
        </TabsList>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Document Library</CardTitle>
                  <CardDescription>
                    All BCMS documents with version control and access tracking
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <AIPowerSearch
                    module="documents"
                    onResults={(results) => setAiSearchResults(results)}
                    onClear={() => setAiSearchResults(null)}
                    placeholder='Try: "find approved policies" or "recovery plans by John"...'
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {DOCUMENT_CATEGORIES.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      {STATUS_OPTIONS.map(status => (
                        <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-sky-700" />
                </div>
              ) : filteredDocuments.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No documents found</p>
                  <p className="text-sm text-slate-400">Try adjusting your filters or upload a new document</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Document</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>Last Modified</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDocuments.map((doc) => {
                      const categoryInfo = getCategoryInfo(doc.category);
                      const statusInfo = getStatusInfo(doc.status);
                      const CategoryIcon = categoryInfo.icon;
                      return (
                        <TableRow key={doc.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${categoryInfo.color}`}>
                                <CategoryIcon className="w-4 h-4" />
                              </div>
                              <div>
                                <p className="font-medium flex items-center gap-1.5">
                                  {doc.title}
                                  <FederatedFromBadge api={api} sourceProfileId={doc.source_profile_id} />
                                </p>
                                <p className="text-xs text-slate-500">
                                  Next review: {doc.next_review || "Not set"}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={categoryInfo.color}>
                              {categoryInfo.name}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <GitBranch className="w-3 h-3 text-slate-400" />
                              <span>v{doc.version}</span>
                            </div>
                          </TableCell>
                          <TableCell>{doc.owner}</TableCell>
                          <TableCell>{doc.last_modified}</TableCell>
                          <TableCell>
                            <Badge className={statusInfo.color}>
                              {statusInfo.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleViewVersions(doc)}
                                title="View versions"
                                data-testid={`doc-versions-${doc.id}`}
                              >
                                <History className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleDownload(doc)}
                                title="Download"
                                data-testid={`doc-download-${doc.id}`}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDelete(doc)}
                                title="Delete"
                                data-testid={`doc-delete-${doc.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {DOCUMENT_CATEGORIES.map((category) => {
              const Icon = category.icon;
              const count = documents.filter(d => d.category === category.id).length;
              return (
                <Card key={category.id} className="hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <div className={`p-3 rounded-lg ${category.color}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{category.name}</h3>
                        <p className="text-2xl font-bold text-slate-900 mt-1">{count}</p>
                        <p className="text-sm text-slate-500">documents</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Retention Policy Tab */}
        <TabsContent value="retention">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Archive className="w-5 h-5 text-sky-700" />
                Document Retention Policy
              </CardTitle>
              <CardDescription>
                ISO 22301 Clause 7.5 requires documented retention periods for BCMS records
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Document Type</TableHead>
                    <TableHead className="font-semibold">Retention Period</TableHead>
                    <TableHead className="font-semibold">Legal Requirement</TableHead>
                    <TableHead className="font-semibold">Storage Location</TableHead>
                    <TableHead className="font-semibold">Access Level</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">BC Policies</TableCell>
                    <TableCell><Badge>Permanent</Badge></TableCell>
                    <TableCell>ISO 22301, Regulatory</TableCell>
                    <TableCell>Document Management System</TableCell>
                    <TableCell><Badge variant="outline"><Lock className="w-3 h-3 mr-1" />Restricted</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">BC Plans</TableCell>
                    <TableCell><Badge>7 Years</Badge></TableCell>
                    <TableCell>ISO 22301</TableCell>
                    <TableCell>Document Management System</TableCell>
                    <TableCell><Badge variant="outline"><Lock className="w-3 h-3 mr-1" />Restricted</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">BIA Reports</TableCell>
                    <TableCell><Badge>7 Years</Badge></TableCell>
                    <TableCell>ISO 22301, Audit</TableCell>
                    <TableCell>Document Management System</TableCell>
                    <TableCell><Badge variant="outline"><Lock className="w-3 h-3 mr-1" />Restricted</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Risk Assessments</TableCell>
                    <TableCell><Badge>5 Years</Badge></TableCell>
                    <TableCell>ISO 22301</TableCell>
                    <TableCell>Document Management System</TableCell>
                    <TableCell><Badge variant="outline"><Lock className="w-3 h-3 mr-1" />Restricted</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Exercise Reports</TableCell>
                    <TableCell><Badge>5 Years</Badge></TableCell>
                    <TableCell>ISO 22301</TableCell>
                    <TableCell>Document Management System</TableCell>
                    <TableCell><Badge variant="outline"><Unlock className="w-3 h-3 mr-1" />Team</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Training Records</TableCell>
                    <TableCell><Badge>3 Years</Badge></TableCell>
                    <TableCell>ISO 22301</TableCell>
                    <TableCell>HR System / DMS</TableCell>
                    <TableCell><Badge variant="outline"><Unlock className="w-3 h-3 mr-1" />Team</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Audit Reports</TableCell>
                    <TableCell><Badge>7 Years</Badge></TableCell>
                    <TableCell>ISO 22301, Regulatory</TableCell>
                    <TableCell>Document Management System</TableCell>
                    <TableCell><Badge variant="outline"><Lock className="w-3 h-3 mr-1" />Restricted</Badge></TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="font-semibold text-amber-900 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Retention Requirements
                </h4>
                <ul className="mt-2 text-sm text-amber-800 space-y-1">
                  <li>• All documents must be retained for the minimum period specified above</li>
                  <li>• Superseded versions must be marked as archived but retained</li>
                  <li>• Destruction of documents requires documented approval</li>
                  <li>• Legal hold may extend retention periods during litigation</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Document Dialog */}
      {/* Upload Document Dialog — TurboTax Progressive Disclosure */}
      <StepDialog
        open={showUploadDialog}
        onOpenChange={(open) => { setShowUploadDialog(open); if (!open) setUploadStep(0); }}
        title="Upload Document"
        currentStep={uploadStep}
        onStepChange={setUploadStep}
        onSubmit={handleUpload}
        onCancel={() => setShowUploadDialog(false)}
        submitLabel={saving ? "Uploading..." : "Upload Document"}
        submitDisabled={saving || !formData.title || !formData.category}
        saving={saving}
        canAdvance={
          uploadStep === 0 ? !!formData.title && !!formData.category :
          true
        }
        steps={[
          { id: "document", title: "Document", subtitle: "What is this document about and what category does it fall under?", isComplete: !!formData.title && !!formData.category },
          { id: "details", title: "Details", subtitle: "Who owns this document and what is it about?", isComplete: !!formData.owner },
          { id: "governance", title: "Governance", subtitle: "Define retention, review schedule, and approval status.", isComplete: !!formData.retention_period },
        ]}
      >
        {/* Step 0: Document */}
        {uploadStep === 0 && (
          <div className="space-y-4" data-testid="step-document">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Document Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Business Continuity Policy v2.0"
                data-testid="doc-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Category *</Label>
              <div className="space-y-2">
                {DOCUMENT_CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, category: cat.id })}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      formData.category === cat.id
                        ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    <p className="font-medium text-sm">{cat.name}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Details */}
        {uploadStep === 1 && (
          <div className="space-y-4" data-testid="step-details">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Owner</Label>
              <Input
                value={formData.owner}
                onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                placeholder="Document owner name"
                data-testid="owner-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Description</Label>
              <Textarea
                value={formData.description || ""}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this document"
                rows={3}
                data-testid="doc-description-input"
              />
            </div>
          </div>
        )}

        {/* Step 2: Governance */}
        {uploadStep === 2 && (
          <div className="space-y-4" data-testid="step-governance">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Retention Period</Label>
              <Select
                value={formData.retention_period}
                onValueChange={(value) => setFormData({ ...formData, retention_period: value })}
              >
                <SelectTrigger data-testid="retention-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RETENTION_PERIODS.map(period => (
                    <SelectItem key={period.value} value={period.value}>{period.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Next Review Date</Label>
              <Input
                type="date"
                value={formData.next_review_date}
                onChange={(e) => setFormData({ ...formData, next_review_date: e.target.value })}
                data-testid="review-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(status => (
                    <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </StepDialog>

      {/* Version History Dialog */}
      <Dialog open={showVersionDialog} onOpenChange={setShowVersionDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-sky-700" />
              Version History
            </DialogTitle>
            <DialogDescription>
              {selectedDocument?.title}
            </DialogDescription>
          </DialogHeader>

          {selectedDocument && (
            <div className="py-4">
              {selectedDocument.versions.length === 0 ? (
                <div className="text-center py-8">
                  <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No version history available</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Version</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Author</TableHead>
                      <TableHead>Changes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedDocument.versions.map((version, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Badge variant={index === 0 ? "default" : "outline"}>
                            v{version.version}
                          </Badge>
                        </TableCell>
                        <TableCell>{version.date}</TableCell>
                        <TableCell>{version.author}</TableCell>
                        <TableCell className="text-sm text-slate-600">{version.changes}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
